import React, { useEffect, useRef } from 'react';
import {
  ArrowRight, Building2, Store, Wrench, Flame, Filter, Waves, Bath, CarFront,
  Utensils, Coffee, Sparkles, Stethoscope, ShoppingBag, UtensilsCrossed,
} from 'lucide-react';

const SERVICES = [
  { label: 'Commercial Shopfit Plumbing', href: '/services', Icon: Building2 },
  { label: 'Retail & Hospitality Fit-Outs', href: '/industries/restaurants', Icon: Store },
  { label: 'New Commercial Builds', href: '/services', Icon: Wrench },
  { label: 'Commercial Kitchen Plumbing', href: '/industries/commercial-kitchens', Icon: Flame },
  { label: 'Grease Trap Installation & Replacement', href: '/grease-pit-plumbing-brisbane', Icon: Filter },
  { label: 'Commercial Drainage Systems', href: '/services', Icon: Waves },
  { label: 'Commercial Bathroom Plumbing', href: '/commercial-bathroom-plumbing-brisbane', Icon: Bath },
  { label: 'Commercial Car Wash Plumbing', href: '/commercial-car-wash-plumbing-brisbane', Icon: CarFront },
];

const INDUSTRIES = [
  { label: 'Restaurants', href: '/industries/restaurants', Icon: Utensils },
  { label: 'Cafés', href: '/industries/cafes', Icon: Coffee },
  { label: 'Beauty & Nail Salons', href: '/industries/beauty-nail-salons', Icon: Sparkles },
  { label: 'Medical Centres', href: '/industries/medical-centres', Icon: Stethoscope },
  { label: 'Offices', href: '/industries/offices', Icon: Building2 },
  { label: 'Retail', href: '/industries/retail', Icon: ShoppingBag },
  { label: 'Commercial Kitchens', href: '/industries/commercial-kitchens', Icon: UtensilsCrossed },
  { label: 'Shopping Centre Tenancies', href: '/industries/shopping-centre-tenancies', Icon: Store },
];

function NavRow({ Icon, label, href, last }) {
  return (
    <a
      href={href}
      className="group relative flex items-center justify-between gap-4 px-4 py-4 rounded-lg transition-all duration-300"
      style={{
        border: '1px solid transparent',
        ...(last ? {} : { marginBottom: '6px' }),
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'rgba(37,99,235,0.55)';
        e.currentTarget.style.background = 'rgba(37,99,235,0.06)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'transparent';
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <div className="flex items-center gap-4 min-w-0">
        <Icon className="w-6 h-6 text-gray-400 group-hover:text-[#2563eb] transition-colors duration-300 flex-shrink-0" strokeWidth={1.5} />
        <span className="text-gray-200 text-[1.0625rem] font-medium group-hover:text-white transition-colors duration-300 truncate">{label}</span>
      </div>
      <span
        className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 transition-all duration-300 group-hover:bg-[#2563eb] group-hover:border-[#2563eb]"
        style={{ border: '1px solid rgba(148,163,184,0.3)' }}
      >
        <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-white group-hover:translate-x-[2px] transition-all duration-300" />
      </span>
    </a>
  );
}

function Panel({ eyebrow, heading, description, items }) {
  return (
    <div
      className="group/panel flex flex-col h-full rounded-[18px] transition-all duration-300 hover:-translate-y-[3px]"
      style={{
        background: 'rgba(12,12,20,0.6)',
        border: '1px solid rgba(255,255,255,0.07)',
        padding: '44px',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), inset 0 0 60px rgba(37,99,235,0.02)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 0 30px rgba(37,99,235,0.10), inset 0 1px 0 rgba(255,255,255,0.05)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'inset 0 1px 0 rgba(255,255,255,0.04), inset 0 0 60px rgba(37,99,235,0.02)'; }}
    >
      <p className="text-[#60a5fa] font-bold tracking-[0.28em] uppercase text-xs mb-4">{eyebrow}</p>
      <h3 className="text-white font-bold text-[1.75rem] md:text-[2rem] tracking-tight leading-[1.1] mb-3">{heading}</h3>
      <p className="text-gray-400 text-[0.9375rem] leading-[1.7] mb-9 max-w-sm">{description}</p>
      <div className="flex-1 flex flex-col">
        {items.map((item, i) => (
          <NavRow key={item.label} {...item} last={i === items.length - 1} />
        ))}
      </div>
    </div>
  );
}

export default function ServicesIndustriesSection() {
  const rootRef = useRef(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const els = root.querySelectorAll('.reveal-row');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target); }
      });
    }, { threshold: 0.1 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <section className="relative px-6 overflow-hidden" style={{ background: '#080810', paddingTop: '120px', paddingBottom: '120px' }}>
      {/* Blueprint grid */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ opacity: 0.05 }}
        preserveAspectRatio="xMidYMid slice"
        viewBox="0 0 1200 1000"
        aria-hidden="true"
      >
        <defs>
          <pattern id="siGrid" width="64" height="64" patternUnits="userSpaceOnUse">
            <path d="M 64 0 L 0 0 0 64" fill="none" stroke="#3b82f6" strokeWidth="0.6" />
          </pattern>
        </defs>
        <rect width="1200" height="1000" fill="url(#siGrid)" />
        {/* Corner drafting marks */}
        <g stroke="#3b82f6" fill="none" strokeWidth="0.8" opacity="0.6">
          <circle cx="80" cy="80" r="22" /><circle cx="80" cy="80" r="4" />
          <circle cx="1120" cy="80" r="22" /><circle cx="1120" cy="80" r="4" />
          <circle cx="80" cy="920" r="22" /><circle cx="80" cy="920" r="4" />
          <circle cx="1120" cy="920" r="22" /><circle cx="1120" cy="920" r="4" />
          <line x1="80" y1="40" x2="80" y2="58" /><line x1="80" y1="102" x2="80" y2="120" />
          <line x1="40" y1="80" x2="58" y2="80" /><line x1="102" y1="80" x2="120" y2="80" />
          <line x1="1120" y1="40" x2="1120" y2="58" /><line x1="1120" y1="102" x2="1120" y2="120" />
          <line x1="1160" y1="80" x2="1142" y2="80" /><line x1="1098" y1="80" x2="1080" y2="80" />
        </g>
      </svg>

      <div ref={rootRef} className="relative max-w-[1280px] mx-auto">
        {/* Header */}
        <div className="max-w-3xl mb-28 reveal-row">
          <p className="text-[#60a5fa] font-bold tracking-[0.28em] uppercase text-sm mb-5">Commercial Plumbing Expertise</p>
          <h2 className="text-[2.625rem] md:text-[3.5rem] font-bold text-white leading-[1.1] tracking-tight mb-6">
            Built for Every Commercial Project
          </h2>
          <div className="mb-10" style={{ width: '90px', height: '2px', background: 'linear-gradient(90deg, #2563eb, rgba(37,99,235,0.1))' }} />
          <p className="text-gray-300 text-[1.125rem] leading-[1.8]" style={{ maxWidth: '650px' }}>
            Oliver &amp; Sons Plumbing delivers commercial plumbing across a wide range of project types and industries throughout South East Queensland. Whether you&apos;re constructing a new commercial building, fitting out a restaurant, medical centre or office, we complete our plumbing scope in accordance with the approved hydraulic design while coordinating closely with builders, shopfitters and other trades.
          </p>
        </div>

        {/* Two panels with gradient divider */}
        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-0 reveal-row">
          <div className="lg:pr-10">
            <Panel
              eyebrow="Services"
              heading="Our Services"
              description="Explore our commercial plumbing services from new builds through to specialist fit-out installations."
              items={SERVICES}
            />
          </div>
          <div className="lg:pl-10">
            <Panel
              eyebrow="Industries"
              heading="Industries We Build With"
              description="Discover industry-specific plumbing solutions designed for different commercial environments."
              items={INDUSTRIES}
            />
          </div>
          {/* Gradient divider with glowing dot */}
          <div
            className="hidden lg:flex absolute top-6 bottom-6 left-1/2 -translate-x-1/2 flex-col items-center justify-center pointer-events-none"
            style={{ width: '1px', background: 'linear-gradient(to bottom, transparent, rgba(37,99,235,0.35), transparent)' }}
          >
            <span
              className="rounded-full"
              style={{ width: '7px', height: '7px', background: '#3b82f6', boxShadow: '0 0 10px rgba(37,99,235,0.8), 0 0 20px rgba(37,99,235,0.4)' }}
            />
          </div>
        </div>

        {/* Centred buttons */}
        <div className="mt-14 flex flex-col sm:flex-row gap-4 justify-center reveal-row">
          <a
            href="/services"
            className="inline-flex items-center justify-center gap-2 text-white font-bold text-[0.9375rem] tracking-wide py-4 px-10 rounded-md transition-all duration-300 hover:-translate-y-0.5"
            style={{ background: '#2563eb', minWidth: '240px' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#1d4ed8'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#2563eb'; }}
          >
            View All Services <ArrowRight className="w-4 h-4" />
          </a>
          <a
            href="/industries"
            className="inline-flex items-center justify-center gap-2 text-white font-bold text-[0.9375rem] tracking-wide py-4 px-10 rounded-md transition-all duration-300 hover:-translate-y-0.5 border border-[#2563eb]/50 hover:border-[#2563eb] hover:bg-[#2563eb]/10"
            style={{ minWidth: '240px' }}
          >
            View All Industries <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Subtle section-ending divider */}
      <div className="relative max-w-[1280px] mx-auto mt-24" style={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(37,99,235,0.15), transparent)' }} />
    </section>
  );
}
